import { useEffect, useState, useRef } from 'react';
import api from '@/lib/api';
import { UserPlus, ArrowDownToLine, ArrowUpFromLine, ShieldCheck, Check, X, Activity, Megaphone } from 'lucide-react';

// Compact relative-time formatter — "45s ago", "12m ago", "3h ago", "2d ago".
function ago(iso) {
  if (!iso) return '—';
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// Per-kind icon + colour + human label. Keep this fully data-driven so adding
// a new event kind on the backend requires zero code changes here.
const KIND = {
  register:          { icon: UserPlus, color: '#4285F4', label: 'signed up' },
  'deposit.submit':  { icon: ArrowDownToLine, color: '#F0B90B', label: 'submitted deposit' },
  'deposit.approve': { icon: Check, color: '#0ECB81', label: 'deposit approved' },
  'deposit.reject':  { icon: X, color: '#F6465D', label: 'deposit rejected' },
  'withdraw.submit': { icon: ArrowUpFromLine, color: '#F0B90B', label: 'requested withdrawal' },
  'withdraw.approve':{ icon: Check, color: '#0ECB81', label: 'withdrawal approved' },
  'withdraw.reject': { icon: X, color: '#F6465D', label: 'withdrawal rejected' },
  'kyc.submit':      { icon: ShieldCheck, color: '#F0B90B', label: 'submitted KYC' },
  'kyc.approve':     { icon: ShieldCheck, color: '#0ECB81', label: 'KYC approved' },
  'kyc.reject':      { icon: ShieldCheck, color: '#F6465D', label: 'KYC rejected' },
  'banner.publish':  { icon: Megaphone, color: '#8B5CF6', label: 'banner published' },
};

// Live admin activity feed — polls every 5s so the admin's Overview tab feels
// alive without WebSocket infra. Auto-pauses when the tab is hidden.
export default function AdminActivityFeed() {
  const [events, setEvents] = useState([]);
  const [flashIds, setFlashIds] = useState(new Set());
  const knownIds = useRef(new Set());
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/admin/activity/recent?limit=40');
      const arr = r.data.events || [];
      // Flash rows that weren't in the previous snapshot
      const newSet = new Set();
      arr.forEach(e => {
        if (!knownIds.current.has(e.activity_id)) newSet.add(e.activity_id);
        knownIds.current.add(e.activity_id);
      });
      setEvents(arr);
      if (newSet.size > 0 && events.length > 0) {
        setFlashIds(newSet);
        setTimeout(() => setFlashIds(new Set()), 2200);
      }
    } catch (_e) {}
  };

  useEffect(() => {
    load();
    const start = () => { if (!timerRef.current) timerRef.current = setInterval(load, 5000); };
    const stop = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
    start();
    const onVis = () => document.hidden ? stop() : (load(), start());
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="border border-[#2B3139] bg-[#181A20] rounded-sm" data-testid="admin-activity-feed">
      <div className="px-4 py-3 border-b border-[#2B3139] flex items-center justify-between">
        <div className="text-xs uppercase font-mono text-[#848E9C] flex items-center gap-1">
          <Activity className="w-3 h-3" /> Live Activity
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-[#0ECB81] animate-pulse" />
          <span className="text-[10px] uppercase font-mono text-[#0ECB81]">Live · refresh 5s</span>
        </div>
      </div>
      {events.length === 0 ? (
        <div className="p-6 text-center text-[#5E6673] text-xs">No activity yet. New events (signups, deposits, KYC, withdrawals) will appear here in real time.</div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto divide-y divide-[#2B3139]/60">
          {events.map(e => {
            const cfg = KIND[e.kind] || { icon: Activity, color: '#848E9C', label: e.kind };
            const Icon = cfg.icon;
            const isNew = flashIds.has(e.activity_id);
            return (
              <div key={e.activity_id}
                   className={`px-4 py-2.5 flex items-center gap-3 text-xs transition-colors ${isNew ? 'bg-[#FCD535]/10' : 'hover:bg-[#0B0E11]/40'}`}
                   data-testid={`activity-row-${e.kind}`}>
                <div className="w-7 h-7 rounded-full grid place-items-center flex-shrink-0" style={{ background: `${cfg.color}22` }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white truncate">
                    <span className="text-[#FCD535] font-mono text-[11px]">{e.subject_email || e.subject_user_id || 'system'}</span>
                    <span className="text-[#848E9C] mx-1">·</span>
                    <span>{cfg.label}</span>
                    {e.meta?.amount && (
                      <span className="text-[#B7BDC6] font-mono ml-1">
                        {e.meta.amount} {e.meta.symbol || ''}
                        {e.meta.network && <span className="text-[#5E6673]">/{e.meta.network}</span>}
                      </span>
                    )}
                    {e.meta?.tier && <span className="text-[#B7BDC6] font-mono ml-1">Tier {e.meta.tier}</span>}
                    {e.meta?.reason && <span className="text-[#F6465D] italic ml-1 truncate">· {e.meta.reason}</span>}
                  </div>
                </div>
                <div className="text-[10px] font-mono text-[#5E6673] flex-shrink-0" data-testid={`activity-time-${e.activity_id}`}>
                  {ago(e.at)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
